import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../i18n/LanguageContext';

const Container = styled.div`
  max-width: 900px;
  margin: 40px auto;
  padding: 0 20px;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #666;
  text-align: center;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
  padding-bottom: 10px;
  border-bottom: 1px solid #eaeaea;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 20px;
  color: #444;
`;

const List = styled.ul`
  margin-bottom: 20px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 10px;
  color: #444;
`;

const ContactInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 5px;
  margin-top: 30px;
`;

const PrivacyPolicy: React.FC = () => {
  const { language } = useLanguage();
  const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

  return (
    <Container>
      <Title>{language === 'zh' ? '隐私政策' : 'Privacy Policy'}</Title>
      <LastUpdated>
        {language === 'zh' 
          ? `最后更新日期: ${currentDate}` 
          : `Last Updated: ${currentDate}`}
      </LastUpdated>

      {language === 'zh' ? (
        <>
          <Section>
            <SectionTitle>介绍</SectionTitle>
            <Paragraph>
              SheetMate（"我们"、"我们的"或"本公司"）尊重您的隐私，并致力于保护您的个人数据。本隐私政策将告知您我们如何处理您的个人数据，以及您的隐私权利和法律保护。
            </Paragraph>
            <Paragraph>
              我们的扩展程序旨在提供强大的 Google Sheets 单元格内容预览功能，包括 Markdown 渲染、LaTeX 公式支持、JSON 格式化以及链接预览等。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>我们收集的数据</SectionTitle>
            <Paragraph>
              我们可能收集以下类型的数据：
            </Paragraph>
            <List>
              <ListItem>
                <strong>账户信息：</strong> 当您创建账户时，我们会收集您的电子邮件地址和用户名。
              </ListItem>
              <ListItem>
                <strong>身份验证数据：</strong> 我们使用 Firebase Authentication 进行用户身份验证。
              </ListItem>
              <ListItem>
                <strong>订阅信息：</strong> 如果您订阅我们的付费服务，我们会通过 Stripe 收集必要的付款信息。
              </ListItem>
              <ListItem>
                <strong>使用数据：</strong> 我们收集有关您如何使用我们的扩展程序的数据，包括功能使用频率和错误报告。
              </ListItem>
              <ListItem>
                <strong>技术数据：</strong> 包括您的浏览器类型、版本和其他设备信息。
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>数据使用方式</SectionTitle>
            <Paragraph>
              我们使用您的数据的主要目的包括：
            </Paragraph>
            <List>
              <ListItem>提供、维护和改进我们的扩展程序功能</ListItem>
              <ListItem>处理订阅付款和管理您的账户</ListItem>
              <ListItem>向您发送与服务相关的通知</ListItem>
              <ListItem>提供客户支持并回应您的查询</ListItem>
              <ListItem>检测、预防和解决技术问题</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>数据存储</SectionTitle>
            <Paragraph>
              我们使用 Google Firebase 作为我们的数据存储解决方案。所有数据均在符合行业标准的安全措施下存储。我们仅在必要时保留您的信息，或根据法律要求保留。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>数据共享</SectionTitle>
            <Paragraph>
              我们不会在未经您同意的情况下与第三方分享您的个人数据，除非：
            </Paragraph>
            <List>
              <ListItem>需要与服务提供商（如 Firebase 和 Stripe）共享以提供我们的服务</ListItem>
              <ListItem>遵守法律义务</ListItem>
              <ListItem>保护 SheetMate 或用户的权利、财产或安全</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>跨境数据传输</SectionTitle>
            <Paragraph>
              您的数据可能会被传输并存储在您所在国家、省份、州或其他政府管辖区域以外的计算机服务器上。我们会确保您的数据得到与本隐私政策一致的保护。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>您的数据权利</SectionTitle>
            <Paragraph>
              根据您所在地区的适用法律，您可能拥有以下权利：
            </Paragraph>
            <List>
              <ListItem>访问您的个人数据</ListItem>
              <ListItem>更正不准确的数据</ListItem>
              <ListItem>删除您的数据</ListItem>
              <ListItem>限制或反对我们处理您的数据</ListItem>
              <ListItem>数据可携性</ListItem>
            </List>
            <Paragraph>
              如需行使这些权利，请通过下面提供的联系方式与我们联系。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>安全措施</SectionTitle>
            <Paragraph>
              我们实施了适当的技术和组织措施，以保护您的个人数据免遭意外丢失、未经授权的访问、使用或披露。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>儿童隐私</SectionTitle>
            <Paragraph>
              我们的服务不面向13岁以下的儿童。我们不会故意收集13岁以下儿童的个人数据。如果您发现我们可能收集了来自儿童的数据，请立即联系我们。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>第三方链接</SectionTitle>
            <Paragraph>
              我们的扩展程序可能包含指向第三方网站或服务的链接。我们不对这些第三方的隐私做法负责，并建议您查看他们的隐私政策。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>隐私政策变更</SectionTitle>
            <Paragraph>
              我们可能会不时更新我们的隐私政策。我们会在此页面发布任何更改，并在进行重大更改时通知您。
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>联系我们</SectionTitle>
            <Paragraph>
              如果您对本隐私政策有任何疑问、意见或请求，请通过以下方式联系我们：
            </Paragraph>
            <ContactInfo>
              <Paragraph>
                <strong>邮箱：</strong> sheetmate.ai@gmail.com
              </Paragraph>
            </ContactInfo>
          </Section>
        </>
      ) : (
        <>
          <Section>
            <SectionTitle>Introduction</SectionTitle>
            <Paragraph>
              SheetMate ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. This privacy policy will inform you about how we handle your personal data and tell you about your privacy rights and legal protections.
            </Paragraph>
            <Paragraph>
              Our extension is designed to provide powerful Google Sheets cell content preview features, including Markdown rendering, LaTeX formula support, JSON formatting, and link previews.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Data We Collect</SectionTitle>
            <Paragraph>
              We may collect the following types of data:
            </Paragraph>
            <List>
              <ListItem>
                <strong>Account Information:</strong> When you create an account, we collect your email address and username.
              </ListItem>
              <ListItem>
                <strong>Authentication Data:</strong> We use Firebase Authentication for user authentication.
              </ListItem>
              <ListItem>
                <strong>Subscription Information:</strong> If you subscribe to our paid services, we collect necessary payment information through Stripe.
              </ListItem>
              <ListItem>
                <strong>Usage Data:</strong> We collect data about how you use our extension, including feature usage frequency and error reports.
              </ListItem>
              <ListItem>
                <strong>Technical Data:</strong> Including your browser type, version, and other device information.
              </ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>How We Use Your Data</SectionTitle>
            <Paragraph>
              The primary purposes for which we use your data include:
            </Paragraph>
            <List>
              <ListItem>To provide, maintain, and improve our extension functionality</ListItem>
              <ListItem>To process subscription payments and manage your account</ListItem>
              <ListItem>To send you service-related notifications</ListItem>
              <ListItem>To provide customer support and respond to your inquiries</ListItem>
              <ListItem>To detect, prevent, and address technical issues</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>Data Storage</SectionTitle>
            <Paragraph>
              We use Google Firebase as our data storage solution. All data is stored with industry-standard security measures in place. We retain your information only for as long as necessary or as required by law.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Data Sharing</SectionTitle>
            <Paragraph>
              We do not share your personal data with third parties without your consent, except:
            </Paragraph>
            <List>
              <ListItem>When necessary with service providers (such as Firebase and Stripe) to provide our services</ListItem>
              <ListItem>To comply with legal obligations</ListItem>
              <ListItem>To protect the rights, property, or safety of SheetMate or our users</ListItem>
            </List>
          </Section>

          <Section>
            <SectionTitle>International Data Transfers</SectionTitle>
            <Paragraph>
              Your data may be transferred to and stored on computers located outside of your state, province, country, or other governmental jurisdiction. We will ensure your data is protected consistent with this Privacy Policy.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Your Data Rights</SectionTitle>
            <Paragraph>
              Depending on your location, you may have the following rights under applicable laws:
            </Paragraph>
            <List>
              <ListItem>Access to your personal data</ListItem>
              <ListItem>Correction of inaccurate data</ListItem>
              <ListItem>Deletion of your data</ListItem>
              <ListItem>Restriction or objection to our processing of your data</ListItem>
              <ListItem>Data portability</ListItem>
            </List>
            <Paragraph>
              To exercise these rights, please contact us using the contact information provided below.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Security Measures</SectionTitle>
            <Paragraph>
              We have implemented appropriate technical and organizational measures to protect your personal data against accidental loss, unauthorized access, use, or disclosure.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Children's Privacy</SectionTitle>
            <Paragraph>
              Our services are not directed to children under the age of 13. We do not knowingly collect personal data from children under 13. If you become aware that we may have collected data from a child, please contact us immediately.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Third-Party Links</SectionTitle>
            <Paragraph>
              Our extension may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties and recommend that you review their privacy policies.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Changes to This Privacy Policy</SectionTitle>
            <Paragraph>
              We may update our Privacy Policy from time to time. We will post any changes on this page and notify you of significant changes.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Contact Us</SectionTitle>
            <Paragraph>
              If you have any questions, comments, or requests regarding this Privacy Policy, please contact us at:
            </Paragraph>
            <ContactInfo>
              <Paragraph>
                <strong>Email:</strong> sheetmate.ai@gmail.com
              </Paragraph>
            </ContactInfo>
          </Section>
        </>
      )}
    </Container>
  );
};

export default PrivacyPolicy; 